import { createClient } from '@/lib/supabase/client'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { SupabaseClient, User, Session } from '@supabase/supabase-js'

/**
 * HMAC-signed cookie mirror for the Supabase access token.
 *
 * 23.04.2026 (Codex simulated-workflow W2-1): the cookie-value is now
 * server-signed via `POST /api/auth/issue-session`. The raw access_token
 * by itself is no longer trusted by the SSR-loader — an attacker with a
 * stolen token cannot forge a valid cookie without the server's HMAC
 * secret.
 *
 * TOKEN_REFRESHED events go through the same exchange, so the cookie
 * stays signed at every rotation.
 */
// Cookie-Krieg-Fix 19.07.2026: Demo-Instanz nutzt eigene Cookie-Namen
// (Inline-Env-Check wie sidebar.tsx — packages/ui kann nicht aus apps/
// importieren). Ohne VITE_DEMO_MODE unverändert 'sp-access-token'.
const ACCESS_TOKEN_COOKIE =
  import.meta.env.VITE_DEMO_MODE === 'true' ? 'sp-access-token-demo' : 'sp-access-token'
const TOKEN_COOKIE_DOMAIN = '.emonse.com'

// DEV-BYPASS Round 2 (25.04.2026, re-applied 08.05.2026 nach CF 429-Lockout
// erzwang Fallback auf lokalen Dev). Triple-guard. Browser rejected
// `Domain=.emonse.com; Secure` Cookies auf localhost → Login-Loop weil
// TOKEN_REFRESHED bei jedem Re-Mount den Cookie überschrieb mit Domain-Attr.
function isLocalDev(): boolean {
  if (typeof import.meta.env?.DEV !== 'undefined' && !import.meta.env.DEV) return false
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '::1'
}

async function fetchSignedCookieValue(
  accessToken: string,
): Promise<{ cookieValue: string; expiresAt: number } | null> {
  try {
    const res = await fetch('/api/auth/issue-session', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
    if (!res.ok) return null
    const body = (await res.json().catch(() => null)) as {
      cookieValue?: string
      expiresAt?: number
    } | null
    if (!body?.cookieValue) return null
    return { cookieValue: body.cookieValue, expiresAt: body.expiresAt || 0 }
  } catch {
    return null
  }
}

// Dedupe state — module-scoped so repeated calls within the same page session
// share the result. Live-finding 23.04.2026 (fatih's network tab): post-login
// the cookie-write fired 3× back-to-back (init Promise.all, then INITIAL_SESSION,
// then SIGNED_IN — each writes the SAME token from the SAME session). 3 × ~320 ms
// = ~1 sec wasted on redundant /api/auth/issue-session HMAC roundtrips.
//
// `lastSignedToken` skips the API call entirely when the same token is re-asked.
// `pendingWrite` coalesces concurrent writes for the same token into one call so
// the 2nd and 3rd onAuthStateChange callbacks await the in-flight first one
// instead of all racing the API.
let lastSignedToken: string | null = null
let pendingWrite: Promise<void> | null = null

async function writeAccessTokenCookie(session: Session | null): Promise<void> {
  if (typeof document === 'undefined') return
  if (!session?.access_token) {
    if (isLocalDev()) {
      document.cookie = `${ACCESS_TOKEN_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
    } else {
      document.cookie = `${ACCESS_TOKEN_COOKIE}=; Path=/; Domain=${TOKEN_COOKIE_DOMAIN}; Max-Age=0; SameSite=Lax; Secure`
    }
    lastSignedToken = null
    pendingWrite = null
    return
  }
  if (session.access_token === lastSignedToken) return
  if (pendingWrite) {
    await pendingWrite
    if (session.access_token === lastSignedToken) return
  }
  // DEV-BYPASS: skip /api/auth/issue-session HMAC roundtrip (kein
  // SESSION_COOKIE_SECRET lokal → 500). Schreibe rohen Token ohne
  // Domain=/Secure damit Browser den Cookie auf localhost akzeptiert.
  if (isLocalDev()) {
    const nowSec = Math.floor(Date.now() / 1000)
    const ttl = session.expires_at ? Math.max(60, session.expires_at - nowSec) : 3300
    document.cookie = `${ACCESS_TOKEN_COOKIE}=${encodeURIComponent(session.access_token)}; Path=/; Max-Age=${ttl}; SameSite=Lax`
    lastSignedToken = session.access_token
    return
  }
  const tokenAtStart = session.access_token
  pendingWrite = (async () => {
    const signed = await fetchSignedCookieValue(tokenAtStart)
    if (!signed?.cookieValue) {
      console.warn('[session-provider] issue-session failed — cookie not written')
      return
    }
    const nowSec = Math.floor(Date.now() / 1000)
    const effectiveExpiresAt = signed.expiresAt || session.expires_at || 0
    const ttl = effectiveExpiresAt ? Math.max(60, effectiveExpiresAt - nowSec) : 3300
    const value = encodeURIComponent(signed.cookieValue)
    document.cookie = `${ACCESS_TOKEN_COOKIE}=${value}; Path=/; Domain=${TOKEN_COOKIE_DOMAIN}; Max-Age=${ttl}; SameSite=Lax; Secure`
    lastSignedToken = tokenAtStart
  })()
  try {
    await pendingWrite
  } finally {
    pendingWrite = null
  }
}

interface AuthContextType {
  user: User | null
  loading: boolean
  supabase: SupabaseClient | null
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, supabase: null })

export function useUser() {
  const { user, loading } = useContext(AuthContext)
  return { user, loading }
}

/** Access the memoized Supabase client (for Realtime subscriptions etc.) */
export function useSupabase() {
  return useContext(AuthContext).supabase
}

export function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const supabase = useMemo(() => {
    try {
      return createClient()
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    // 03.05.2026: session-only auto-signOut REMOVED.
    //
    // Old behaviour: if `emonse_session_only=true` was in localStorage AND
    // `emonse_tab_active` was missing from sessionStorage, we'd auto-signOut
    // the user. The intent: log them out when they unchecked "Stay signed
    // in" and then closed the whole browser.
    //
    // The bug: iOS Safari kills background tabs under memory pressure,
    // which zeroes sessionStorage WITHOUT closing the browser. Users who
    // had simply switched apps were getting auto-logged-out — fatih
    // reported 3× involuntary logouts in one day. Codex pre-push review
    // also identified a Supabase persistSession=true regression: probing
    // for a live session is no signal because supabase-js always keeps
    // the token in localStorage independent of our session-only flag.
    //
    // Proper fix would be to switch to a sessionStorage-backed Supabase
    // client when the user picks no-stay (separate spawn-task). Until
    // then we honour "Stay signed in unchecked" only by NOT setting the
    // flag in login.tsx (the manual sign-out button still works). We
    // still clear any stale `emonse_session_only` left from older builds
    // so the legacy state can't bite anyone.
    try {
      localStorage.removeItem('emonse_session_only')
      sessionStorage.removeItem('emonse_tab_active')
    } catch {
      /* SSR / private mode */
    }

    // 15.05.2026 ROOT-CAUSE companion fix — 3×/Tag involuntary logout
    // (fatih). Supabase autoRefreshToken only ticks while the tab is
    // foreground-active; a backgrounded tab's timers are suspended so the
    // access token AND its sp-access-token mirror cookie (Max-Age == token
    // exp) silently die after ~1h. The DETERMINISTIC safety-net is the
    // /login silent-recovery (apps/shoppanel-app/src/routes/_auth/
    // login.tsx). This pair just makes the common in-tab case seamless so
    // the user rarely even sees the /login bounce:
    //   (a) proactively refresh REFRESH_MARGIN_MS before expiry, and
    //   (b) force a refresh the instant the tab becomes visible/focused
    //       again, awaiting the cookie re-mint so an immediate navigation
    //       carries the fresh token.
    const REFRESH_MARGIN_MS = 5 * 60 * 1000
    let refreshTimer: ReturnType<typeof setTimeout> | null = null

    function scheduleProactiveRefresh(s: Session | null) {
      if (refreshTimer) {
        clearTimeout(refreshTimer)
        refreshTimer = null
      }
      if (!supabase || !s?.expires_at) return
      // Tokens are ~1h so this delay is always well under setTimeout's cap.
      const delay = Math.max(0, s.expires_at * 1000 - Date.now() - REFRESH_MARGIN_MS)
      refreshTimer = setTimeout(() => {
        // → TOKEN_REFRESHED → onAuthStateChange below re-writes the cookie
        //   AND reschedules the next proactive refresh.
        void supabase.auth.refreshSession()
      }, delay)
    }

    async function refreshIfStale() {
      if (!supabase || typeof document === 'undefined') return
      if (document.visibilityState !== 'visible') return
      const {
        data: { session: s },
      } = await supabase.auth.getSession()
      if (!s?.expires_at) return
      if (s.expires_at * 1000 - Date.now() > REFRESH_MARGIN_MS) return
      const { data: refreshed } = await supabase.auth.refreshSession()
      if (refreshed?.session) {
        // Await so a navigation right after the user returns carries the
        // fresh token (onAuthStateChange's cookie write isn't awaited).
        await writeAccessTokenCookie(refreshed.session)
      }
    }

    const onVisible = () => {
      void refreshIfStale()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    bootstrapSession()

    // Get initial session — AND mirror the access_token into a non-httpOnly
    // cookie so the SSR loader on shoppanel-ssr can read it for bootstrap
    // (B3 fix 24.04.2026 — without this mirror the SSR getSession() returns
    // null because supabase-js stores the session in localStorage, not in
    // a cookie the CF Worker can see on the inbound request).
    function bootstrapSession() {
      if (!supabase) return
      Promise.all([supabase.auth.getUser(), supabase.auth.getSession()]).then(
        async ([{ data: userData }, { data: sessionData }]) => {
          setUser(userData.user)
          await writeAccessTokenCookie(sessionData.session)
          setLoading(false)
        },
      )
    }

    // Listen for auth changes — but preserve richer metadata from getUser().
    // JWT-decoded session.user often lacks custom_name/full_name that the
    // /auth/v1/user endpoint returns. Only replace user if the new data
    // actually has metadata, or if the user signed out (session=null).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Keep the SSR-readable cookie in sync on every auth transition:
      // SIGNED_IN / TOKEN_REFRESHED / USER_UPDATED → write fresh token,
      // SIGNED_OUT → clear (writeAccessTokenCookie handles the null case).
      void writeAccessTokenCookie(session)
      // Reschedule the proactive refresh off every transition (incl. the
      // initial INITIAL_SESSION event and every TOKEN_REFRESHED rotation).
      scheduleProactiveRefresh(session)
      if (!session?.user) {
        setUser(null)
      } else {
        setUser((prev) => {
          const newMeta = session.user.user_metadata
          const prevMeta = prev?.user_metadata
          // Keep the richer user if the new one has no custom_name but the old one does
          if (prev && prevMeta?.custom_name && !newMeta?.custom_name) return prev
          return session.user
        })
      }
      setLoading(false)
    })

    return () => {
      subscription.unsubscribe()
      if (refreshTimer) clearTimeout(refreshTimer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [supabase])

  return <AuthContext.Provider value={{ user, loading, supabase }}>{children}</AuthContext.Provider>
}
